import * as Sentry from "@sentry/react"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { useCreateOutlookIntegrationMutation } from "modules/settings/hooks/outlook/useCreateOutlookIntegrationMutation"
import { useDiscoverOutlookMailboxesQuery } from "modules/settings/hooks/outlook/useDiscoverOutlookMailboxesQuery"
import { useSetOutlookMailboxesMutation } from "modules/settings/hooks/outlook/useSetOutlookMailboxesMutation"
import { useValidateOutlookMailboxMutation } from "modules/settings/hooks/outlook/useValidateOutlookMailboxMutation"
import { Loading } from "modules/shared/components/Loading"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Route as AddOutlookIntegrationRoute } from "routes/_base/_auth/settings/channels/outlook/new"

type ConnectedAccount = {
  outlookAccountId: string
  email: string
  accountEmail: string
  isPersonalAccount: boolean
}
type Mailbox = { email: string; displayName: string | null }

export const AddOutlookIntegration = () => {
  const { t } = useTranslation()
  const { code, error } = AddOutlookIntegrationRoute.useSearch()
  const navigate = useNavigate()

  const createMutation = useCreateOutlookIntegrationMutation()
  const validateMutation = useValidateOutlookMailboxMutation()
  const setMutation = useSetOutlookMailboxesMutation()

  const hasStarted = useRef(false)
  const [account, setAccount] = useState<ConnectedAccount | null>(null)
  const [manual, setManual] = useState<string[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [input, setInput] = useState("")
  const [validationError, setValidationError] = useState<string | null>(null)

  const discoverQuery = useDiscoverOutlookMailboxesQuery(account?.outlookAccountId, {
    enabled: !!account && !account.isPersonalAccount,
  })
  const discovered = useMemo<Mailbox[]>(
    () =>
      (discoverQuery.data?.mailboxes ?? []).map((mailbox) => ({
        ...mailbox,
        email: mailbox.email.toLowerCase(),
      })),
    [discoverQuery.data],
  )

  const onClose = useCallback(() => {
    navigate({ to: "/settings/channels/outlook", replace: true })
  }, [navigate])

  useEffect(() => {
    if (hasStarted.current) return
    hasStarted.current = true

    if (error === "access_denied" || code === "access_denied" || !code) {
      onClose()
      return
    }

    createMutation
      .mutateAsync({ code })
      .then((data) => {
        const email = data.mailboxEmail.toLowerCase()
        setAccount({
          outlookAccountId: data.outlookAccountId,
          email,
          accountEmail: data.accountEmail.toLowerCase(),
          isPersonalAccount: data.isPersonalAccount,
        })
        setSelected([email])
      })
      .catch((error) => {
        Sentry.captureException(error)
        onClose()
      })
  }, [code, error, createMutation, onClose])

  const toggle = (email: string) =>
    setSelected((prev) =>
      prev.includes(email) ? prev.filter((item) => item !== email) : [...prev, email],
    )

  const handleAdd = async () => {
    const email = input.trim().toLowerCase()
    setValidationError(null)

    if (!email || !account) return

    if (email === account.accountEmail) {
      setValidationError(t("settings.integrations.outlook.selectMailboxes.errors.self"))
      setInput("")
      return
    }

    const isKnown =
      email === account.email ||
      manual.includes(email) ||
      discovered.some((mailbox) => mailbox.email === email)
    if (isKnown) {
      if (!selected.includes(email)) toggle(email)
      setInput("")
      return
    }

    const { status } = await validateMutation.mutateAsync({
      outlookAccountId: account.outlookAccountId,
      email,
    })

    if (status === "ok") {
      setManual((prev) => [...prev, email])
      setSelected((prev) => [...prev, email])
      setInput("")
      return
    }

    setValidationError(t(`settings.integrations.outlook.selectMailboxes.errors.${status}`))
  }

  const handleDone = async () => {
    if (!account || !selected.length) return
    await setMutation.mutateAsync({ outlookAccountId: account.outlookAccountId, emails: selected })
    onClose()
  }

  const mailboxRow = (email: string, label: string | null, isOwn: boolean) => (
    <label
      key={email}
      className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2"
    >
      <Checkbox checked={selected.includes(email)} onCheckedChange={() => toggle(email)} />
      <span className="flex-1 text-sm text-foreground">{email}</span>
      {isOwn ? (
        <span className="text-xs text-muted-foreground">
          {t("settings.integrations.outlook.selectMailboxes.ownMailbox")}
        </span>
      ) : (
        label && <span className="text-xs text-muted-foreground">{label}</span>
      )}
    </label>
  )

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        {!account ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("settings.integrations.outlook.addIntegration.title")}</DialogTitle>
              <DialogDescription>
                {t("settings.integrations.outlook.addIntegration.description")}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <Loading size={6} />
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t("settings.integrations.outlook.selectMailboxes.title")}</DialogTitle>
              <DialogDescription>
                {t("settings.integrations.outlook.selectMailboxes.description")}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2">
              {mailboxRow(account.email, null, true)}
              {account.accountEmail !== account.email && (
                <p className="-mt-1 pl-3 text-xs text-muted-foreground">
                  {t("settings.integrations.outlook.selectMailboxes.signedInAs", {
                    email: account.accountEmail,
                  })}
                </p>
              )}
              {account.isPersonalAccount ? (
                <p className="text-xs text-muted-foreground">
                  {t("settings.integrations.outlook.selectMailboxes.personalOnly")}
                </p>
              ) : (
                <>
                  {discovered.map((mailbox) =>
                    mailboxRow(mailbox.email, mailbox.displayName, false),
                  )}
                  {manual.map((email) => mailboxRow(email, null, false))}

                  {discoverQuery.isFetching && (
                    <p className="text-xs text-muted-foreground">
                      {t("settings.integrations.outlook.selectMailboxes.discovering")}
                    </p>
                  )}

                  <div className="mt-2 flex items-start gap-2">
                    <div className="flex-1">
                      <Input
                        type="email"
                        value={input}
                        placeholder={t(
                          "settings.integrations.outlook.selectMailboxes.addPlaceholder",
                        )}
                        onChange={(event) => setInput(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault()
                            void handleAdd()
                          }
                        }}
                      />
                      {validationError && (
                        <p className="mt-1 text-xs text-destructive">{validationError}</p>
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => void handleAdd()}
                      disabled={!input.trim() || validateMutation.isPending}
                    >
                      {t("settings.integrations.outlook.selectMailboxes.add")}
                    </Button>
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button
                onClick={() => void handleDone()}
                disabled={!selected.length || setMutation.isPending}
              >
                {t("settings.integrations.outlook.selectMailboxes.done")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
